import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { get, set } from "idb-keyval";
import { useQueryClient } from "@tanstack/react-query";
import type { OfflineMutationName } from "@/lib/offline-registry";

// The mutation registry pulls in every server-function client on the site, and
// the toast library is only needed once something actually syncs. Both are
// loaded on demand so a first-time visitor to a marketing page never downloads
// or parses them.
async function loadMutation(name: OfflineMutationName) {
  const { OFFLINE_MUTATIONS } = await import("@/lib/offline-registry");
  return (OFFLINE_MUTATIONS as Record<string, (args: { data: unknown }) => Promise<unknown>>)[
    name
  ];
}

async function notify(kind: "success" | "message", text: string) {
  const { toast } = await import("sonner");
  toast[kind](text);
}

type QueuedMutation = {
  id: string;
  name: OfflineMutationName;
  data: unknown;
  queuedAt: number;
};

const OUTBOX_KEY = "founders.outbox.v1";

/** Guards against two drains running at once (timer + "online" event). */
let flushInFlight: Promise<void> | null = null;

async function readOutbox(): Promise<QueuedMutation[]> {
  try {
    return (await get<QueuedMutation[]>(OUTBOX_KEY)) ?? [];
  } catch {
    return [];
  }
}

async function writeOutbox(items: QueuedMutation[]) {
  try {
    await set(OUTBOX_KEY, items);
  } catch {
    /* storage unavailable — nothing else we can do */
  }
}

function newIdemKey() {
  const c = globalThis.crypto;
  if (c && "randomUUID" in c) return c.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Attach a one-time key to an object payload (leaves other shapes alone). */
function withIdemKey(data: unknown): unknown {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  const obj = data as Record<string, unknown>;
  if (typeof obj["idemKey"] === "string") return obj;
  return { ...obj, idemKey: newIdemKey() };
}

/**
 * Identifiers a queued change is "about", used to keep a create and its later
 * delete together. `localRef` is minted by the UI for optimistic records; `id`
 * covers changes that already have a server row.
 */
function refsOf(item: QueuedMutation): string[] {
  const data = item.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return [];
  const obj = data as Record<string, unknown>;
  const refs: string[] = [];
  if (typeof obj["localRef"] === "string" && obj["localRef"]) refs.push(obj["localRef"]);
  if (typeof obj["id"] === "string" && obj["id"]) refs.push(obj["id"]);
  return refs;
}

/** Mint a reference the UI can attach to a create *and* its follow-up delete. */
export function newLocalRef(): string {
  const c = globalThis.crypto;
  if (c && "randomUUID" in c) return `local-${c.randomUUID()}`;
  return `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isNetworkError(error: unknown) {
  if (typeof navigator !== "undefined" && !navigator.onLine) return true;
  const message = error instanceof Error ? error.message : String(error ?? "");
  return /failed to fetch|networkerror|load failed|network request failed|fetch failed/i.test(
    message,
  );
}

type OfflineContextValue = {
  online: boolean;
  pending: number;
  enqueue: (name: OfflineMutationName, data: unknown) => Promise<void>;
  flush: () => Promise<void>;
};

const OfflineContext = createContext<OfflineContextValue | null>(null);

export function OfflineProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [online, setOnline] = useState(true);
  const [pending, setPending] = useState(0);

  const refreshCount = useCallback(async () => {
    setPending((await readOutbox()).length);
  }, []);

  const enqueue = useCallback(
    async (name: OfflineMutationName, data: unknown) => {
      const items = await readOutbox();
      items.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name,
        data,
        queuedAt: Date.now(),
      });
      await writeOutbox(items);
      setPending(items.length);
    },
    [],
  );

  const flush = useCallback(async () => {
    if (typeof navigator !== "undefined" && !navigator.onLine) return;
    // Only one drain may run at a time. Without this, the 30s timer and the
    // "online" event could replay the same queued change twice.
    if (flushInFlight) return flushInFlight;
    flushInFlight = (async () => {
    let items = await readOutbox();
    if (items.length === 0) return;

    let synced = 0;
    let dropped = 0;

    while (items.length > 0) {
      const next = items[0]!;
      const fn = await loadMutation(next.name);
      if (!fn) {
        items = items.slice(1);
        dropped += 1;
        await writeOutbox(items);
        continue;
      }
      try {
        await fn({ data: next.data });
        items = items.slice(1);
        synced += 1;
        await writeOutbox(items);
      } catch (error) {
        if (isNetworkError(error)) break; // still offline — keep everything queued
        // Server rejected it (e.g. the record is gone). Drop it so the queue can
        // drain — and drop anything else queued for the same record. Otherwise a
        // create that never landed would leave its follow-up delete behind, and
        // that delete would fail too, leaving an orphan the user can't clear.
        const refs = refsOf(next);
        items = items.slice(1);
        dropped += 1;
        if (refs.length > 0) {
          const before = items.length;
          items = items.filter((queued) => !refsOf(queued).some((r) => refs.includes(r)));
          dropped += before - items.length;
        }
        await writeOutbox(items);
      }
    }

    setPending(items.length);
    if (synced > 0) {
      await queryClient.invalidateQueries();
      await notify(
        "success",
        synced === 1 ? "1 saved change synced" : `${synced} saved changes synced`,
      );
    }
    if (dropped > 0) {
      await notify(
        "message",
        dropped === 1
          ? "1 queued change couldn't be applied and was cleared."
          : `${dropped} queued changes couldn't be applied and were cleared.`,
      );
    }
    })().finally(() => {
      flushInFlight = null;
    });
    return flushInFlight;
  }, [queryClient]);

  useEffect(() => {
    setOnline(navigator.onLine);
    void refreshCount();
    void flush();

    const goOnline = () => {
      setOnline(true);
      void flush();
    };
    const goOffline = () => setOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    const timer = window.setInterval(() => void flush(), 30_000);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      window.clearInterval(timer);
    };
  }, [flush, refreshCount]);

  const value = useMemo(
    () => ({ online, pending, enqueue, flush }),
    [online, pending, enqueue, flush],
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export function useOffline() {
  const ctx = useContext(OfflineContext);
  if (!ctx) {
    return {
      online: true,
      pending: 0,
      enqueue: async () => {},
      flush: async () => {},
    } satisfies OfflineContextValue;
  }
  return ctx;
}

/**
 * Wraps a queue-safe server mutation. When there's no signal (or the request
 * fails for network reasons) the change is stored on the device and replayed
 * automatically once you're back online.
 */
export function useOfflineFn<N extends OfflineMutationName>(name: N) {
  const { enqueue } = useOffline();

  return useCallback(
    async (args: { data: unknown }) => {
      // One key per user action, minted before the first attempt and reused by
      // every replay, so the server can recognise a repeat and ignore it.
      const payload = withIdemKey(args.data);
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        await enqueue(name, payload);
        await notify("success", "Saved on this device — it'll sync when you have signal");
        return null;
      }
      try {
        const fn = await loadMutation(name);
        if (!fn) throw new Error(`Unknown offline mutation: ${name}`);
        return await fn({ data: payload });
      } catch (error) {
        if (isNetworkError(error)) {
          await enqueue(name, payload);
          await notify("success", "Saved on this device — it'll sync when you have signal");
          return null;
        }
        throw error;
      }
    },
    [enqueue, name],
  );
}

export function OfflineBanner() {
  const { online, pending } = useOffline();
  if (online && pending === 0) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-sm border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
      <span className="micro-label text-foreground">{online ? "Syncing" : "Offline"}</span>
      <span>
        {online
          ? "Catching up on changes saved while you were out of signal."
          : "You can keep writing — everything saves on this device."}
      </span>
      {pending > 0 && (
        <span className="text-foreground/80">
          {pending} change{pending === 1 ? "" : "s"} waiting
        </span>
      )}
    </div>
  );
}
